import React from 'react';
import { Language } from '../types';
import { X } from 'lucide-react';
import { TRANSLATIONS } from '../translations';
import { CONFIG } from '../config';

interface ModalProps {
    onClose: () => void;
    language: Language;
}

// Base Modal Component
const Modal: React.FC<{ onClose: () => void; title: string; children: React.ReactNode }> = ({
    onClose,
    title,
    children
}) => (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
        <div
            className="modal-content bg-[#0d0d0d] border border-[#4a7c59]/50 rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
        >
            <div className="flex items-center justify-between p-4 border-b border-[#4a7c59]/30">
                <h2 className="text-xl font-bold text-[#6b9b7a]">{title}</h2>
                <button
                    onClick={onClose}
                    className="text-[#888] hover:text-white transition-colors p-1"
                >
                    <X size={24} />
                </button>
            </div>
            <div className="p-4">
                {children}
            </div>
        </div>
    </div>
);

// Help Modal
export function HelpModal({ onClose, language }: ModalProps) {
    const t = TRANSLATIONS[language];

    return (
        <Modal onClose={onClose} title={t.guide}>
            <div className="space-y-4 text-[#e8e4db]">
                <p className="opacity-80">{t.help_intro}</p>

                {/* Indicators */}
                <div>
                    <h3 className="font-semibold text-[#6b9b7a] mb-2">{t.indicators}</h3>
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded status-correct"></div>
                            <span>{t.exact_match}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded status-partial"></div>
                            <span>{t.partial_match}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded status-wrong"></div>
                            <span>{t.incorrect}</span>
                        </div>
                    </div>
                </div>

                {/* Chronology */}
                <div>
                    <h3 className="font-semibold text-[#6b9b7a] mb-2">{t.chronology}</h3>
                    <p className="text-sm opacity-70">{t.chronology_desc}</p>
                </div>

                {/* Rules */}
                <div className="space-y-3 text-sm">
                    <div>
                        <h4 className="font-semibold text-white">{t.rule_classic_header}</h4>
                        <ul className="list-disc list-inside opacity-70 space-y-1 mt-1">
                            <li>{t.rule_classic_gender}</li>
                            <li>{t.rule_classic_arc}</li>
                            <li>{t.rule_classic_affiliation}</li>
                            <li>{t.rule_classic_species}</li>
                            <li>{t.rule_classic_height}</li>
                            <li>{t.rule_classic_status}</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white">{t.rule_quote_header}</h4>
                        <p className="opacity-70">{t.rule_quote_text}</p>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white">{t.rule_image_header}</h4>
                        <p className="opacity-70">{t.rule_image_text}</p>
                    </div>
                    <p className="text-[#6b9b7a]">{t.rule_guesses}</p>
                </div>
            </div>
        </Modal>
    );
}

// Stats Modal
export function StatsModal({ onClose, language }: ModalProps) {
    const t = TRANSLATIONS[language];
    const [timeLeft, setTimeLeft] = React.useState('');

    const stats = React.useMemo(() => {
        const saved = localStorage.getItem(CONFIG.STORAGE_KEYS.STATS);
        return saved ? JSON.parse(saved) : {
            played: 0,
            won: 0,
            streak: 0,
            maxStreak: 0
        };
    }, []);

    // Real-time countdown
    React.useEffect(() => {
        const tick = () => {
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);

            const diff = tomorrow.getTime() - now.getTime();
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, []);

    const winRate = stats.played > 0
        ? Math.round((stats.won / stats.played) * 100)
        : 0;

    return (
        <Modal onClose={onClose} title={t.stats}>
            <div className="grid grid-cols-4 gap-4 text-center mb-4">
                <div className="bg-[#1a1a1a] rounded-lg p-4">
                    <p className="text-2xl font-bold text-white">{stats.played}</p>
                    <p className="text-xs text-[#888]">{t.played}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-4">
                    <p className="text-2xl font-bold text-white">{winRate}%</p>
                    <p className="text-xs text-[#888]">{t.win_rate}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-4">
                    <p className="text-2xl font-bold text-[#6b9b7a]">{stats.streak}</p>
                    <p className="text-xs text-[#888]">{t.streak}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-4">
                    <p className="text-2xl font-bold text-white">{stats.maxStreak}</p>
                    <p className="text-xs text-[#888]">{t.max_streak}</p>
                </div>
            </div>
            {/* Next Character Countdown */}
            <div className="bg-[#1a1a1a] rounded-lg p-4 text-center border border-[#4a7c59]/30">
                <p className="text-xs text-[#888] uppercase tracking-widest mb-1">{t.next_character}</p>
                <p className="text-2xl font-mono font-bold text-[#6b9b7a]">{timeLeft}</p>
            </div>
        </Modal>
    );
}

// Settings Modal
export function SettingsModal({ onClose, language }: ModalProps) {
    const t = TRANSLATIONS[language];

    const handleReset = () => {
        if (window.confirm(t.setting_reset_confirm)) {
            localStorage.removeItem(CONFIG.STORAGE_KEYS.STATS);
            // Clear all game states - matches format used in App.tsx
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(`${CONFIG.STORAGE_KEYS.GAME_STATE}-`)) {
                    localStorage.removeItem(key);
                }
            });
            window.location.reload();
        }
    };

    return (
        <Modal onClose={onClose} title={t.settings}>
            <div className="space-y-4">
                {/* Reset Data */}
                <div className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
                    <div>
                        <p className="font-medium text-white">{t.setting_reset}</p>
                        <p className="text-sm text-[#888]">{t.setting_reset_desc}</p>
                    </div>
                    <button
                        onClick={handleReset}
                        className="px-4 py-2 bg-red-900/50 hover:bg-red-900 text-red-200 rounded-lg transition-colors text-sm"
                    >
                        {t.reset_button}
                    </button>
                </div>

                {/* Feedback Link */}
                <a
                    href={`mailto:${CONFIG.CONTACT_EMAIL}`}
                    className="block p-3 bg-[#1a1a1a] rounded-lg hover:bg-[#2a2a2a] transition-colors"
                >
                    <p className="font-medium text-white">{t.feedback}</p>
                    <p className="text-sm text-[#888]">{CONFIG.CONTACT_EMAIL}</p>
                </a>
            </div>
        </Modal>
    );
}
